import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import CountryFlag, { getCountryFlagByCode } from '../CountryFlag';
import { api } from '../../api';

/**
 * Cache for countries to avoid repeated API calls
 * @type {Array|null}
 */
let countriesCache = null;

/**
 * Promise for ongoing API request to prevent duplicate calls
 * @type {Promise|null}
 */
let cachePromise = null;

/**
 * Function to get countries from cache or API
 * @returns {Promise<Array>} Array of country objects
 */
const getCountries = async () => {
  if (countriesCache) {
    return countriesCache;
  }
  
  if (cachePromise) {
    return cachePromise;
  }
  
  cachePromise = api.master.getActiveCountries()
    .then(response => {
      if (response?.data?.countries) {
        countriesCache = response.data.countries;
        return countriesCache;
      }
      return [];
    })
    .catch(error => {
      console.error('Error fetching countries:', error);
      return [];
    })
    .finally(() => {
      cachePromise = null;
    });
    
  return cachePromise;
};

/**
 * Function to find country by ID
 * @param {string|number} countryId - The country ID to lookup
 * @returns {Promise<Object|null>} Country object or null if not found
 */
const findCountryById = async (countryId) => {
  if (!countryId) return null;
  
  const countries = await getCountries();
  console.log("countries", countries, "countryId", countryId);
  return countries.find(country => country.id === parseInt(countryId));
};

/**
 * NationalityDisplay Component
 * Displays nationality with country flag in a standardized format
 */
const NationalityDisplay = ({ 
  nationality, 
  nationalityId, // country_id to lookup country information
  variant = "body2", 
  color = "textSecondary",
  showLabel = true,
  inline = false,
  flagSize = '48x36', // Default flag size
  ...props 
}) => {
  const [countryInfo, setCountryInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Effect to fetch country information based on nationalityId
  useEffect(() => {
    const fetchCountryInfo = async () => {
      if (nationalityId) {
        setLoading(true);
        try {
          console.log("ARKA - Fetching country for nationalityId:", nationalityId);
          const country = await findCountryById(nationalityId);
          if (country) {
            setCountryInfo({
              name: country.country_name,
              nationality: country.nationality,
              country_code: country.country_code,
              id: country.id
            });
          } else {
            console.warn("No country found for nationalityId:", nationalityId);
            setCountryInfo(null);
          }
        } catch (error) {
          console.error('Error fetching country info:', error);
          setCountryInfo(null);
        } finally {
          setLoading(false);
        }
      } else {
        // If no nationalityId, clear any existing country info
        setCountryInfo(null);
        setLoading(false);
      }
    };

    fetchCountryInfo();
  }, [nationalityId]); // Only depend on nationalityId, not nationality

  const renderNationality = useCallback(() => {
    // If loading, show loading indicator
    if (loading) {
      return "Loading...";
    }

    // Use provided nationality first, then fallback to country lookup
    const displayNationality = nationality || countryInfo?.nationality;
    const displayCountryName = nationality || countryInfo?.name;
    const displayCountryCode = countryInfo?.country_code;
    // console.log("displayCountryCode", displayCountryCode);
    if (!displayNationality && !displayCountryName) {
      return showLabel ? "N/A" : null;
    }

    const countryNameForFlag = displayCountryName || displayNationality;

    if (inline) {
      return (
        <>
          {getCountryFlagByCode(displayCountryCode, flagSize)} {displayNationality || displayCountryName}
        </>
      );
    }

    return (
      <CountryFlag 
        countryId={nationalityId}
        countryName={countryNameForFlag} 
        showName={true}
        variant={variant}
        color="inherit"
        component="span"
      />
    );
  }, [countryInfo, nationality]);

  if (inline) {
    return (
      <Typography variant={variant} color={color} component="span" {...props}>
        {showLabel && "Nationality: "}
        <strong>{renderNationality()}</strong>
      </Typography>
    );
  }

  return (
    <Typography variant={variant} color={color} {...props}>
      {showLabel && "Nationality: "}
      <strong>{renderNationality()}</strong>
    </Typography>
  );
};

// PropTypes for better type checking and documentation
NationalityDisplay.propTypes = {
  nationality: PropTypes.string,
  nationalityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.string,
  color: PropTypes.string,
  showLabel: PropTypes.bool,
  inline: PropTypes.bool,
  flagSize: PropTypes.string,
};

// Default props
NationalityDisplay.defaultProps = {
  nationality: null,
  nationalityId: null,
  variant: "body2",
  color: "textSecondary",
  showLabel: true,
  inline: false,
  flagSize: '48x36',
};

/**
 * Function to clear countries cache (useful for testing or data refresh)
 */
export const clearCountriesCache = () => {
  countriesCache = null;
  cachePromise = null;
};

/**
 * Function to get all countries (useful for other components)
 * @returns {Promise<Array>} Array of country objects
 */
export const getAllCountries = () => getCountries();

export default NationalityDisplay;
